/**
 * Workflow Version Diff Utilities
 *
 * Computes detailed differences between workflow versions for display
 * in a Google Docs-style version history view.
 */

import type { Workflow, WorkflowDefinition, WorkflowNode } from "@/stores/workflowsStore";

/**
 * Represents a single change in a workflow version
 */
export interface VersionChange {
  type: "added" | "removed" | "modified";
  category: "node" | "connection" | "setting";
  description: string;
  details?: {
    field?: string;
    oldValue?: unknown;
    newValue?: unknown;
    nodeType?: string;
  };
}

/**
 * Summary of changes between two versions
 */
export interface VersionDiffSummary {
  /** Total number of changes */
  totalChanges: number;
  /** Number of nodes added */
  nodesAdded: number;
  /** Number of nodes removed */
  nodesRemoved: number;
  /** Number of nodes modified */
  nodesModified: number;
  /** Number of connections changed */
  connectionsChanged: number;
  /** Number of settings changed */
  settingsChanged: number;
  /** Whether this is the initial version (v1) */
  isInitialVersion: boolean;
  /** Detailed list of all changes */
  changes: VersionChange[];
}

/**
 * Compute the diff between two workflow versions.
 * Returns a summary of all changes from the previous version to the current.
 *
 * @param current - The current workflow version
 * @param previous - The previous workflow version (null for v1)
 */
export function computeVersionDiff(current: Workflow, previous: Workflow | null): VersionDiffSummary {
  // Initial version - no previous to compare
  if (!previous) {
    const nodeCount = Object.keys(current.definition?.nodes || {}).length;
    return {
      totalChanges: 0,
      nodesAdded: nodeCount,
      nodesRemoved: 0,
      nodesModified: 0,
      connectionsChanged: 0,
      settingsChanged: 0,
      isInitialVersion: true,
      changes: [],
    };
  }

  const changes: VersionChange[] = [];

  // Compare settings
  const settingChanges = compareSettings(current, previous);
  changes.push(...settingChanges);

  // Compare nodes
  const nodeChanges = compareNodes(current.definition?.nodes || {}, previous.definition?.nodes || {});
  changes.push(...nodeChanges);

  // Count changes by type
  const nodesAdded = changes.filter(c => c.category === "node" && c.type === "added").length;
  const nodesRemoved = changes.filter(c => c.category === "node" && c.type === "removed").length;
  const nodesModified = changes.filter(c => c.category === "node" && c.type === "modified").length;
  const connectionsChanged = changes.filter(c => c.category === "connection").length;
  const settingsChanged = changes.filter(c => c.category === "setting").length;

  return {
    totalChanges: changes.length,
    nodesAdded,
    nodesRemoved,
    nodesModified,
    connectionsChanged,
    settingsChanged,
    isInitialVersion: false,
    changes,
  };
}

/**
 * Compare workflow-level settings between versions
 */
function compareSettings(current: Workflow, previous: Workflow): VersionChange[] {
  const changes: VersionChange[] = [];

  if (current.name !== previous.name) {
    changes.push({
      type: "modified",
      category: "setting",
      description: "Workflow name changed",
      details: {
        field: "name",
        oldValue: previous.name,
        newValue: current.name,
      },
    });
  }

  if (current.maxRuns !== previous.maxRuns) {
    changes.push({
      type: "modified",
      category: "setting",
      description: "Max runs changed",
      details: {
        field: "maxRuns",
        oldValue: previous.maxRuns ?? "unlimited",
        newValue: current.maxRuns ?? "unlimited",
      },
    });
  }

  if (current.cooldownMs !== previous.cooldownMs) {
    changes.push({
      type: "modified",
      category: "setting",
      description: "Cooldown changed",
      details: {
        field: "cooldownMs",
        oldValue: previous.cooldownMs,
        newValue: current.cooldownMs,
      },
    });
  }

  return changes;
}

/**
 * Compare workflow nodes between versions
 */
function compareNodes(
  currentNodes: Record<string, WorkflowNode>,
  previousNodes: Record<string, WorkflowNode>,
): VersionChange[] {
  const changes: VersionChange[] = [];

  const currentNodeIds = new Set(Object.keys(currentNodes));
  const previousNodeIds = new Set(Object.keys(previousNodes));

  // Find added nodes
  for (const nodeId of currentNodeIds) {
    if (!previousNodeIds.has(nodeId)) {
      const node = currentNodes[nodeId];
      changes.push({
        type: "added",
        category: "node",
        description: `Added "${node.type}" node`,
        details: {
          nodeType: node.type,
        },
      });
    }
  }

  // Find removed nodes
  for (const nodeId of previousNodeIds) {
    if (!currentNodeIds.has(nodeId)) {
      const node = previousNodes[nodeId];
      changes.push({
        type: "removed",
        category: "node",
        description: `Removed "${node.type}" node`,
        details: {
          nodeType: node.type,
        },
      });
    }
  }

  // Find modified nodes
  for (const nodeId of currentNodeIds) {
    if (previousNodeIds.has(nodeId)) {
      const currentNode = currentNodes[nodeId];
      const previousNode = previousNodes[nodeId];

      const nodeChanges = compareNode(currentNode, previousNode, nodeId);
      changes.push(...nodeChanges);
    }
  }

  return changes;
}

/**
 * Compare a single node between versions
 */
function compareNode(current: WorkflowNode, previous: WorkflowNode, nodeId: string): VersionChange[] {
  const changes: VersionChange[] = [];

  // Check if node type changed (rare but possible)
  if (current.type !== previous.type) {
    changes.push({
      type: "modified",
      category: "node",
      description: `Node type changed from "${previous.type}" to "${current.type}"`,
      details: {
        nodeType: current.type,
        oldValue: previous.type,
        newValue: current.type,
      },
    });
  }

  // Check if payload changed (ignoring position)
  const currentPayload = JSON.stringify(sortObjectKeys(current.payload || {}));
  const previousPayload = JSON.stringify(sortObjectKeys(previous.payload || {}));

  if (currentPayload !== previousPayload) {
    changes.push({
      type: "modified",
      category: "node",
      description: `Modified "${current.type}" node configuration`,
      details: {
        nodeType: current.type,
      },
    });
  }

  // Check if connections changed
  const currentChildren = [...(current.children || [])].sort().join(",");
  const previousChildren = [...(previous.children || [])].sort().join(",");

  if (currentChildren !== previousChildren) {
    changes.push({
      type: "modified",
      category: "connection",
      description: `Changed connections for "${current.type}" node`,
      details: {
        nodeType: current.type,
      },
    });
  }

  return changes;
}

/**
 * Sort object keys for consistent comparison
 */
function sortObjectKeys(obj: Record<string, unknown>): Record<string, unknown> {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item =>
      typeof item === "object" && item !== null ? sortObjectKeys(item as Record<string, unknown>) : item,
    ) as unknown as Record<string, unknown>;
  }

  const sorted: Record<string, unknown> = {};
  const keys = Object.keys(obj).sort();

  for (const key of keys) {
    const value = obj[key];
    sorted[key] =
      typeof value === "object" && value !== null ? sortObjectKeys(value as Record<string, unknown>) : value;
  }

  return sorted;
}

/**
 * Format a diff summary into a human-readable string
 */
export function formatDiffSummary(diff: VersionDiffSummary): string {
  if (diff.isInitialVersion) {
    return "Initial version";
  }

  if (diff.totalChanges === 0) {
    return "No changes";
  }

  const parts: string[] = [];

  if (diff.nodesAdded > 0) {
    parts.push(`+${diff.nodesAdded} node${diff.nodesAdded > 1 ? "s" : ""}`);
  }

  if (diff.nodesRemoved > 0) {
    parts.push(`-${diff.nodesRemoved} node${diff.nodesRemoved > 1 ? "s" : ""}`);
  }

  if (diff.nodesModified > 0) {
    parts.push(`~${diff.nodesModified} modified`);
  }

  if (diff.connectionsChanged > 0) {
    parts.push(`${diff.connectionsChanged} connection${diff.connectionsChanged > 1 ? "s" : ""}`);
  }

  if (diff.settingsChanged > 0) {
    parts.push(`${diff.settingsChanged} setting${diff.settingsChanged > 1 ? "s" : ""}`);
  }

  return parts.join(", ");
}
